"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { track } from "@vercel/analytics";

import {
  adClickSource,
  campaignLabel,
  currentCampaign,
  currentAdClickIds,
  hasAdClickId,
  readAdClickIds,
  readCampaignTag,
  rememberAdClickIds,
  rememberCampaign,
  sameCampaign,
} from "@/lib/campaign-source";
import { cleanSiteRef, currentSiteRef, rememberSiteRef } from "@/lib/site-referral";

let engagedVisitTrackedForTab = false;

/**
 * Remembers where a visitor came from, for the two kinds of link that bring us people:
 * the "Built by Ownly Digital" credit on the sites we build (`?ref=`), and the social
 * posts, bio links and campaigns we publish ourselves (`utm_*`).
 *
 * Neither writes anything to the visitor's device. See the notes at the top of
 * src/lib/site-referral.ts and src/lib/campaign-source.ts for why that constraint is
 * deliberate and what it costs us.
 *
 * `usePathname` and not `useSearchParams` on purpose: the latter forces every page
 * under it into a Suspense boundary, which is a heavy price for a tracker.
 */
export function ReferralTracker() {
  const pathname = usePathname();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    /* ---- client-site credit (`?ref=`) ---------------------------------------- */

    const fromUrl = cleanSiteRef(params.get("ref"));
    if (fromUrl && fromUrl !== currentSiteRef()) {
      rememberSiteRef(fromUrl);
      track("site-referral", { ref: fromUrl });
    }

    /* ---- our own campaigns (`utm_*`) ----------------------------------------- */

    const campaign = readCampaignTag(params);
    if (campaign && !sameCampaign(campaign, currentCampaign())) {
      rememberCampaign(campaign);
      // Vercel Analytics is cookieless, so this needs no consent banner. `source` is
      // its own property rather than only the combined label so the dashboard can group
      // by platform without string-matching.
      track("campaign-arrival", {
        source: campaign.source,
        medium: campaign.medium ?? "none",
        campaign: campaign.campaign ?? "none",
        label: campaignLabel(campaign),
      });
    }

    const clickIds = readAdClickIds(params);
    if (hasAdClickId(clickIds) && !hasAdClickId(currentAdClickIds())) {
      rememberAdClickIds(clickIds);
      track("ad-click-arrival", { source: adClickSource(clickIds) ?? "unknown" });
    }

    /*
     * The `utm_*` tags are NOT written back into the address bar, and that is a
     * deliberate difference from `ref` below.
     *
     * A visitor who copies the URL out of the bar and sends it to someone else would
     * hand us a second visit wearing the first one's tags, and we would count a
     * forwarded link as another Instagram click. Holding the tag in memory instead
     * means the number is a floor rather than a wrong number, which is the more useful
     * of the two. The forms read it straight out of memory via `campaignFields()`, so
     * it survives every client-side navigation on the site; only a hard reload drops it.
     */

    const ref = currentSiteRef();
    if (!ref || params.get("ref") === ref) return;

    // replaceState, not push: the tag is bookkeeping, and it must not put a step in
    // the visitor's back button.
    const url = new URL(window.location.href);
    url.searchParams.set("ref", ref);
    window.history.replaceState(null, "", url.toString());
  }, [pathname]);

  useEffect(() => {
    const source = () =>
      currentCampaign()?.source ?? adClickSource(currentAdClickIds()) ?? currentSiteRef() ?? "direct";

    const markEngaged = () => {
      if (engagedVisitTrackedForTab) return;
      engagedVisitTrackedForTab = true;
      track("engaged-visit", { path: pathname, source: source() });
      window.removeEventListener("pointerdown", markEngaged, true);
      window.removeEventListener("keydown", markEngaged, true);
    };

    const trackConversionClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest<HTMLAnchorElement>("a[href]");
      if (!anchor) return;
      let destination: URL;
      try {
        destination = new URL(anchor.href, window.location.href);
      } catch {
        return;
      }
      if (destination.origin !== window.location.origin) return;
      /* `data-cta` names which button it was ("hero", "sticky-bar", "next-step"), so the funnel
         report can say which placement actually gets pressed. Unlabelled links still count. */
      const placement = anchor.dataset.cta ?? "unlabelled";
      if (anchor.dataset.ctaKind === "guide") {
        track("guide-cta-clicked", { from: pathname, placement, source: source() });
        return;
      }
      if (!["/start", "/book"].includes(destination.pathname)) return;
      track("conversion-cta", {
        from: pathname,
        to: destination.pathname,
        placement,
        source: source(),
      });
    };

    window.addEventListener("pointerdown", markEngaged, true);
    window.addEventListener("keydown", markEngaged, true);
    document.addEventListener("click", trackConversionClick);
    return () => {
      window.removeEventListener("pointerdown", markEngaged, true);
      window.removeEventListener("keydown", markEngaged, true);
      document.removeEventListener("click", trackConversionClick);
    };
  }, [pathname]);

  return null;
}
